import React, { useMemo, useState } from "react";
import { Landmark, Search } from "lucide-react";
import { COLORS } from "@/lib/api";

const ACCION_COLOR = {
  Convencer: COLORS.guinda,
  Movilizar: COLORS.terracota,
  Contener: COLORS.activaciones,
};
const ARENA = "#BEB195";

function Card({ item, highlighted }) {
  const accentColor = ACCION_COLOR[item.accion] || ARENA;
  return (
    <div
      data-testid={`cseccion-card-${item.seccion}`}
      className={`rounded-xl border bg-white p-3.5 transition-shadow hover:shadow-md ${
        highlighted ? "border-guinda ring-2 ring-guinda/30 shadow-md" : "border-slate-200"
      }`}
      style={{ borderLeft: `4px solid ${accentColor}` }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Landmark className="w-3.5 h-3.5 text-guinda" />
          <span className="font-heading font-bold text-slate-900 text-sm">Sección {item.seccion}</span>
        </div>
        {item.accion && (
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-white"
            style={{ background: accentColor }}
          >
            {item.accion}
          </span>
        )}
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-slate-500">Resultado 2024</span>
          <span className="font-semibold text-slate-800 text-right">{item.resultado_2024 || "—"}</span>
        </div>
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-slate-500">Lista nominal</span>
          <span className="font-semibold text-slate-800">
            {item.lista_nominal ? Number(item.lista_nominal).toLocaleString("es-MX") : "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function SeccionInfoCards({ data, selectedValue }) {
  const [q, setQ] = useState("");

  const sorted = useMemo(() => {
    const list = (data || []).slice();
    list.sort((a, b) => String(a.seccion).localeCompare(String(b.seccion), "es", { numeric: true }));
    return list;
  }, [data]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = sorted;
    if (term) {
      list = list.filter(
        (i) =>
          String(i.seccion).toLowerCase().includes(term) ||
          (i.accion || "").toLowerCase().includes(term) ||
          (i.resultado_2024 || "").toLowerCase().includes(term)
      );
    }
    if (selectedValue) {
      const sel = String(selectedValue);
      list = list.slice().sort((a, b) =>
        String(a.seccion) === sel ? -1 : String(b.seccion) === sel ? 1 : 0
      );
    }
    return list;
  }, [sorted, q, selectedValue]);

  if (!data || data.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="cseccion-cards">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-base font-bold text-slate-900">Información por Sección</h3>
          <p className="text-xs text-slate-500">Acción, resultado electoral 2024 y lista nominal</p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="cseccion-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar sección, acción o resultado"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-guinda/30"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-[460px] overflow-y-auto thin-scroll pr-1">
        {filtered.map((item) => (
          <Card key={item.seccion} item={item} highlighted={String(item.seccion) === String(selectedValue)} />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-sm text-slate-400 py-8">
            Sin resultados para "{q}"
          </div>
        )}
      </div>
    </div>
  );
}
